import type {
	CreateHerdMutationRequest,
	UpdateHerdMutationRequest,
} from "@packages/api-client/types/livestockController";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateHerd, useDeleteHerd } from "@/api/livestock";
import { PageHeader } from "@/components/PageHeader";
import { HerdCreatedPanel } from "./-components/HerdCreatedPanel";
import { HerdForm } from "./-components/HerdForm";
import {
	buildHerdReadback,
	computeMissingHerdItems,
	type HerdMissingKey,
} from "./-components/herdSummary";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/livestock/$establishmentId/herds/new",
)({
	staticData: { offline: "ready" },
	component: NewHerdPage,
});

/** The just-created herd, held so the saved state can read it back and undo it. */
interface SavedHerd {
	id: string;
	name: string;
	readback: string;
	missing: HerdMissingKey[];
}

function NewHerdPage() {
	const { orgId, establishmentId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const createMutation = useCreateHerd();
	const deleteMutation = useDeleteHerd();

	const [saved, setSaved] = useState<SavedHerd | null>(null);
	// Remounts the form blank after "Create another" / an undo.
	const [formGeneration, setFormGeneration] = useState(0);

	const goToList = () =>
		navigate({
			to: "/app/org/$orgId/livestock",
			params: { orgId },
			search: { tab: "herds", farm: establishmentId },
		});

	const handleSubmit = async (
		formData: CreateHerdMutationRequest | UpdateHerdMutationRequest,
	) => {
		const data = formData as CreateHerdMutationRequest;
		const created = await createMutation.mutateAsync({
			organizationId: orgId,
			data,
		});
		setSaved({
			id: created.id,
			name: created.name,
			readback: buildHerdReadback(data, t),
			missing: computeMissingHerdItems(data),
		});
		return created;
	};

	const createAnother = () => {
		setSaved(null);
		setFormGeneration((generation) => generation + 1);
	};

	const undoCreate = async () => {
		if (!saved) return;
		await deleteMutation.mutateAsync({
			organizationId: orgId,
			herdId: saved.id,
		});
		createAnother();
	};

	const addAnimals = () => {
		if (!saved) return;
		navigate({
			to: "/app/org/$orgId/livestock/$establishmentId/animals/new",
			params: { orgId, establishmentId },
			search: { herd: saved.id },
		});
	};

	// "Fill in when you know" → the herd's edit form, focused on that field.
	// The management system is a top-of-form chip row, so it needs no focus hint.
	const fillDetail = (key: HerdMissingKey) => {
		if (!saved) return;
		navigate({
			to: "/app/org/$orgId/livestock/$establishmentId/herds/$herdId/edit",
			params: { orgId, establishmentId, herdId: saved.id },
			search: key === "system" ? {} : { focus: key },
		});
	};

	return (
		// `-mt-3` cancels `<main>`'s `p-3` top gutter on phones (see the animal edit page).
		<div className="-mt-3 flex h-full flex-col gap-2 md:mt-0 md:gap-6">
			<PageHeader
				title={t("livestock.herd.create.title")}
				showBackButton
				onBack={goToList}
			/>
			{saved ? (
				<HerdCreatedPanel
					name={saved.name}
					readback={saved.readback}
					missing={saved.missing}
					onAddAnimals={addAnimals}
					onCreateAnother={createAnother}
					onUndo={undoCreate}
					onFillDetail={fillDetail}
					undoPending={deleteMutation.isPending}
				/>
			) : (
				<HerdForm
					key={formGeneration}
					establishmentId={establishmentId}
					onSubmit={handleSubmit}
					isSubmitting={createMutation.isPending}
					onCancel={goToList}
				/>
			)}
		</div>
	);
}
